import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { FileText, Filter, Search, Plus, Calendar, Download, Share2, ChevronLeft, ChevronRight, SlidersHorizontal, Trash2, LayoutTemplate } from 'lucide-react';
import ShareDocumentModal from '../../components/document-viewer/share-document-modal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { format, isToday, isYesterday, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears } from 'date-fns';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { useDocumentCategories } from '../../hooks/useDocumentCategories';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface Category {
  id: string;
  name: string;
}

interface Signatory {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface SigningRequest {
  id: string;
  signatory: Signatory;
  status: string;
}

interface DocumentRecipient {
  id: string;
  name: string;
  email: string;
}

interface Document {
  id: string;
  title: string;
  category?: Category;
  category_id?: string;
  file_path: string;
  created_at: string;
  updated_at: string;
  status: string;
  signing_requests: SigningRequest[];
  recipients?: DocumentRecipient[];
  notes?: string;
  template_id?: number | null;
  template?: { id: number; template_title: string } | null;
}

const Documents: React.FC = () => {
  const { t } = useTranslation(['documents', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse URL parameters
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    return {
      timeFilter: params.get('timeFilter') || 'month',
      categoryFilter: params.get('categoryId') || 'all',
      statusFilter: params.get('status') || 'draft',
      searchTerm: params.get('search') || '',
    };
  };
  
  // State for documents data
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<{ current_page: number; last_page: number; per_page: number; total: number } | null>(null);
  const [timeFilter, setTimeFilter] = useState<string>(getInitialFilters().timeFilter);
  const [categoryFilter, setCategoryFilter] = useState<string>(getInitialFilters().categoryFilter);
  const [statusFilter, setStatusFilter] = useState<string>(getInitialFilters().statusFilter);
  const [searchTerm, setSearchTerm] = useState<string>(getInitialFilters().searchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(getInitialFilters().searchTerm);
  
  // Use our custom hook for categories
  const { categories: fetchedCategories } = useDocumentCategories();
  const { user: currentUser } = useAuthContext();
  
  // Delete modal state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Share modal state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<Document | null>(null);
  
  // Extract categories array from the response if needed
  const categoriesArray = Array.isArray(fetchedCategories) ? fetchedCategories : 
                         (fetchedCategories && 'categories' in fetchedCategories) ? 
                         (fetchedCategories as any).categories : [];

  // Debounce search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update URL when filters or search change
  useEffect(() => {
    const params = new URLSearchParams();
    if (timeFilter !== 'month') params.set('timeFilter', timeFilter);
    if (categoryFilter !== 'all') params.set('categoryId', categoryFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
    
    // Update URL without causing a navigation/reload
    navigate({ search: params.toString() }, { replace: true });
    
    // Fetch documents with the new filters
    fetchDocuments();
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm, currentPage]);

  // Fetch documents data with the latest filters
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get API endpoint
      const endpoint = '/api/documents';
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (statusFilter !== 'all') queryParams.set('status', statusFilter);
      if (categoryFilter !== 'all') queryParams.set('category_id', categoryFilter);
      if (timeFilter !== 'all') queryParams.set('time_period', timeFilter);
      if (debouncedSearchTerm.trim().length >= 3) queryParams.set('search', debouncedSearchTerm.trim());
      queryParams.set('page', currentPage.toString());
      
      // Use fetch with full URL to ensure proper connection
      // Using centralized API configuration
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      
      // Add logging to help debug the API request
      const requestUrl = `${apiUrl}${endpoint}?${queryParams.toString()}`;
      console.log('Making API request to:', requestUrl);
      
      const response = await fetch(
        requestUrl, 
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Documents API response:', data);
      
      if (data) {
        // Laravel pagination returns data in a 'data' property
        setDocuments(data.data || []);
        setPaginationMeta({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        });
      }
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError(err.message || 'Failed to fetch documents data');
    } finally {
      setLoading(false);
    }
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm]);

  // Document statuses for filter dropdown
  const documentStatuses = [
    { value: 'all', label: t('common:status.all') },
    { value: 'draft', label: t('common:status.draft') },
    { value: 'pending', label: t('common:status.pending') },
    { value: 'partially_signed', label: t('common:status.partially_signed') },
    { value: 'completed', label: t('common:status.completed') },
    { value: 'declined', label: t('common:status.declined') }
  ];

  // Time periods for filter dropdown
  const timePeriods = [
    { value: 'week', label: t('common:time.this_week') },
    { value: 'month', label: t('common:time.this_month') },
    { value: 'quarter', label: t('common:time.this_quarter') },
    { value: 'year', label: t('common:time.this_year') },
    { value: 'all', label: t('common:time.all_time') }
  ];

  // Remove client-side filtering since the API is now handling all filtering
  const filteredDocuments = documents;

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">{t('common:status.draft')}</Badge>;
      case 'pending':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{t('common:status.pending')}</Badge>;
      case 'partially_signed':
        return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{t('common:status.partially_signed')}</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t('common:status.completed')}</Badge>;
      case 'declined':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{t('common:status.declined')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };


  // Handle delete document
  const handleDeleteClick = (doc: Document) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    setDeleting(true);
    try {
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      const response = await fetch(`${apiUrl}/api/documents/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete document');
      }
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      fetchDocuments();
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setError(err.message || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  // Format full date for tooltip
  const formatFullDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      return dateString;
    }
  };

  // Format relative date for display
  const formatRelativeDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();

      if (isToday(date)) return format(date, 'h:mm a');
      if (isYesterday(date)) return t('common:time.yesterday');

      const daysDiff = differenceInDays(now, date);
      if (daysDiff < 7) return t('common:time.days_ago', { count: daysDiff });

      const weeksDiff = differenceInWeeks(now, date);
      if (weeksDiff === 1) return t('common:time.last_week');
      if (weeksDiff < 4) return t('common:time.weeks_ago', { count: weeksDiff });

      const monthsDiff = differenceInMonths(now, date);
      if (monthsDiff === 1) return t('common:time.last_month');
      if (monthsDiff < 12) return t('common:time.months_ago', { count: monthsDiff });

      const yearsDiff = differenceInYears(now, date);
      if (yearsDiff === 1) return t('common:time.last_year');
      return t('common:time.years_ago', { count: yearsDiff });
    } catch (err) {
      return dateString;
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Page header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('documents:page_title')}</h1>
          <p className="text-muted-foreground">
            {t('documents:page_subtitle')}
          </p>
        </div>
        
        <div className="flex flex-row gap-3">
          <Button
            variant="outline"
            className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4 text-indigo-500" />
            {showFilters ? t('documents:hide_filters') : t('documents:show_filters')}
          </Button>
          <Link to="/document-editor">
            <Button className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="mr-2 h-4 w-4" />
              {t('documents:new_document')}
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Filters section */}
      {showFilters && <Card className="border-indigo-100 bg-gradient-to-r from-indigo-50/50 via-white to-purple-50/50 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-indigo-100">
              <SlidersHorizontal className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{t('documents:filters_title')}</CardTitle>
              <CardDescription>{t('documents:filters_description')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Time period filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-amber-700">
                <Calendar className="h-3.5 w-3.5" />
                {t('documents:time_period')}
              </label>
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger className="bg-white border-amber-200 focus:ring-amber-300">
                  <SelectValue placeholder={t('documents:select_period')} />
                </SelectTrigger>
                <SelectContent>
                  {timePeriods.map(period => (
                    <SelectItem key={period.value} value={period.value}>
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Category filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-blue-700">
                <FileText className="h-3.5 w-3.5" />
                {t('documents:category')}
              </label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="bg-white border-blue-200 focus:ring-blue-300">
                  <SelectValue placeholder={t('documents:select_category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('documents:all_categories')}</SelectItem>
                  {categoriesArray.map((category: {id: string, name: string}) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-purple-700">
                <Filter className="h-3.5 w-3.5" />
                {t('documents:status')}
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white border-purple-200 focus:ring-purple-300">
                  <SelectValue placeholder={t('documents:select_status')} />
                </SelectTrigger>
                <SelectContent>
                  {documentStatuses.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Search */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-emerald-700">
                <Search className="h-3.5 w-3.5" />
                {t('documents:search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400" />
                <Input 
                  placeholder={t('documents:search_placeholder')} 
                  className="pl-9 bg-white border-emerald-200 focus:ring-emerald-300"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>}
      
      {/* Documents table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button 
                variant="outline" 
                onClick={fetchDocuments} 
                className="mt-4"
              >
                {t('common:actions.try_again')}
              </Button>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('documents:no_documents_title')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('documents:no_documents_message')}
              </p>
              <div className="flex justify-center gap-3">
                {!showFilters && (
                  <Button
                    variant="outline"
                    className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => setShowFilters(true)}
                  >
                    <SlidersHorizontal className="mr-2 h-4 w-4 text-indigo-500" />
                    {t('documents:show_filters')}
                  </Button>
                )}
                <Link to="/document-editor">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('documents:new_document')}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('documents:table_title')}</TableHead>
                    <TableHead>{t('documents:table_category')}</TableHead>
                    <TableHead>{t('documents:table_signer')}</TableHead>
                    <TableHead>{t('documents:table_status')}</TableHead>
                    <TableHead>{t('documents:table_created')}</TableHead>
                    <TableHead className="text-center">{t('documents:table_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow 
                      key={doc.id} 
                      className="cursor-pointer"
                      onClick={() => navigate(
                        doc.status === 'signed' || doc.status === 'completed' || doc.status === 'declined' || doc.status === 'partially_signed'
                          ? `/document-viewer/${doc.id}`
                          : `/document-editor/${doc.id}`
                      )}
                    >
                      <TableCell className="font-medium max-w-[200px] truncate">
                        <span className="flex items-center gap-1.5">
                          {doc.template_id && doc.template && (
                            <span title={doc.template.template_title}>
                              <LayoutTemplate
                                className="h-4 w-4 text-muted-foreground shrink-0"
                              />
                            </span>
                          )}
                          {doc.title}
                        </span>
                      </TableCell>
                      <TableCell>{doc.category?.name || t('documents:uncategorized')}</TableCell>
                      <TableCell>
                        {doc.recipients && doc.recipients.length > 0 ? (
                          <div
                            className="max-w-[150px] truncate text-sm"
                            title={doc.recipients.map((r) => r.name).join(', ')}
                          >
                            {doc.recipients.map((r) =>
                              currentUser && r.email === currentUser.email
                                ? 'Me'
                                : r.name.split(' ')[0]
                            ).join(', ')}
                          </div>
                        ) : doc.signing_requests && doc.signing_requests.length > 0 ? (
                          <div
                            className="max-w-[150px] truncate text-sm"
                            title={doc.signing_requests.map((req) => `${req.signatory.first_name} ${req.signatory.last_name}`).join(', ')}
                          >
                            {doc.signing_requests.map((req) =>
                              currentUser && req.signatory.email === currentUser.email
                                ? 'Me'
                                : req.signatory.first_name
                            ).join(', ')}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">{t('documents:no_signers')}</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      <TableCell className="whitespace-nowrap" title={formatFullDate(doc.created_at)}>{formatRelativeDate(doc.created_at)}</TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 hover:bg-green-50"
                            title={t('common:actions.download')}
                            onClick={async () => {
                              try {
                                const response = await fetch(`${API_BASE_URL}/documents/${doc.id}/stream`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                  },
                                });
                                if (!response.ok) {
                                  if (response.status === 401) { handleUnauthorized(); return; }
                                  throw new Error('Download failed');
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = window.document.createElement('a');
                                a.href = url;
                                a.download = `${doc.title}.pdf`;
                                window.document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              } catch (err) {
                                console.error('Download error:', err);
                              }
                            }}
                          >
                            <Download className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-purple-50"
                            title="Share"
                            onClick={() => {
                              setDocumentToShare(doc);
                              setShareDialogOpen(true);
                            }}
                          >
                            <Share2 className="h-4 w-4 text-purple-600" />
                          </Button>
                          {doc.status.toLowerCase() === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-red-50"
                              title={t('common:actions.delete')}
                              onClick={() => handleDeleteClick(doc)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {paginationMeta && paginationMeta.last_page > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('documents:showing_documents', {
                  start: ((paginationMeta.current_page - 1) * paginationMeta.per_page) + 1,
                  end: Math.min(paginationMeta.current_page * paginationMeta.per_page, paginationMeta.total),
                  total: paginationMeta.total
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('common:actions.previous')}
                </Button>
                <span className="text-sm font-medium">
                  {t('common:pagination.page_of', { current: paginationMeta.current_page, last: paginationMeta.last_page })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= paginationMeta.last_page}
                  onClick={() => setCurrentPage((p) => Math.min(paginationMeta.last_page, p + 1))}
                >
                  {t('common:actions.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Delete confirmation modal */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('documents:delete_title')}</DialogTitle>
            <DialogDescription>
              {t('documents:delete_message', { title: documentToDelete?.title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? t('documents:deleting') : t('documents:delete_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Share document modal */}
      {documentToShare && (
        <ShareDocumentModal
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          documentId={documentToShare.id}
          documentName={documentToShare.title}
        />
      )}
    </div>
  );
};

export default Documents;
